import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, LayoutGrid, Mail, Plus, Rows3, Search, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { NewSolutionSheet } from "@/components/solutions/new-solution-sheet";
import { SolutionCard } from "@/components/solutions/SolutionCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { type PendingInvite, useClaimInvite, usePendingInvites } from "@/hooks/use-members";
import type { Solution } from "@/hooks/use-solutions";
import { useSolutions } from "@/hooks/use-solutions";
import { useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";

const SKELETON_KEYS = ["a", "b", "c", "d", "e", "f"] as const;

export function SolutionsListPage() {
  const { data, isLoading, isError, refetch } = useSolutions();
  const [view, setView] = useState<"tile" | "row">("tile");
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.toLowerCase(), 200);
  const [newOpen, setNewOpen] = useState(false);
  const pendingInvites = usePendingInvites();
  const { data: session } = useSession();
  const isAdmin = session?.user?.role === "admin";

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((s) => {
      if (search) {
        const haystack = `${s.name} ${s.clientName} ${s.summary ?? ""}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [data, search]);

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 space-y-6">
      <PageHeader
        title="Solutions"
        description="Pick an engagement, or start a new one."
        actions={
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button variant="outline" asChild>
                <Link to="/dashboard/settings/users">
                  <ShieldCheck className="h-4 w-4 mr-1.5" />
                  Users
                </Link>
              </Button>
            )}
            <ThemeToggle />
            <Button onClick={() => setNewOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" />
              New solution
            </Button>
          </div>
        }
      />

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search solutions or clients…"
            className="pl-8 h-9 text-sm"
          />
        </div>
        <div className="ml-auto inline-flex items-center rounded-md border border-[var(--color-border)] bg-card p-0.5 h-9">
          <button
            type="button"
            onClick={() => setView("tile")}
            title="Tile view"
            className={cn(
              "inline-flex h-8 px-3 items-center gap-1.5 rounded-[5px] text-xs font-medium transition-colors",
              view === "tile"
                ? "bg-muted text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            Tile
          </button>
          <button
            type="button"
            onClick={() => setView("row")}
            title="Row view"
            className={cn(
              "inline-flex h-8 px-3 items-center gap-1.5 rounded-[5px] text-xs font-medium transition-colors",
              view === "row"
                ? "bg-muted text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Rows3 className="h-3.5 w-3.5" />
            Row
          </button>
        </div>
      </div>

      {isLoading && (
        <div className={view === "tile" ? "grid gap-5 sm:grid-cols-2" : "space-y-2"}>
          {SKELETON_KEYS.map((k) => (
            <Skeleton
              key={k}
              className={cn("rounded-xl", view === "tile" ? "h-[220px]" : "h-[44px]")}
            />
          ))}
        </div>
      )}

      {isError && <ErrorState message="Failed to load solutions" onRetry={() => refetch()} />}

      {pendingInvites.data && pendingInvites.data.length > 0 && (
        <PendingInvitesSection invites={pendingInvites.data} />
      )}

      {data && filtered.length === 0 && (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] p-12 text-center">
          <p className="text-sm text-muted-foreground">
            {data.length === 0
              ? pendingInvites.data && pendingInvites.data.length > 0
                ? "Accept an invite above, or create your first solution."
                : "No solutions yet. Create your first one."
              : "No solutions match this filter."}
          </p>
        </div>
      )}

      {filtered.length > 0 && view === "tile" && (
        <div className="grid gap-5 sm:grid-cols-2">
          {filtered.map((solution) => (
            <SolutionCard key={solution.id} solution={solution} />
          ))}
        </div>
      )}

      {filtered.length > 0 && view === "row" && <RowView solutions={filtered} />}

      <NewSolutionSheet open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}

const STAGE_LABEL: Record<Solution["stage"], string> = {
  sales: "Pre-sale",
  discovery: "Discovery",
  build: "Live build",
};

const STAGE_TONE: Record<Solution["stage"], string> = {
  sales: "bg-[#F1ECFB] text-[#5E3FB0] dark:bg-[#1a0e2e] dark:text-[#b89df0]",
  discovery: "bg-nymbl-sky text-nymbl-navy",
  build: "bg-success-bg text-success",
};

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  return mo < 12 ? `${mo}mo ago` : `${Math.round(mo / 12)}y ago`;
}

function PendingInvitesSection({ invites }: { invites: PendingInvite[] }) {
  const claim = useClaimInvite();
  const navigate = useNavigate();

  async function handleClaim(inviteId: string) {
    try {
      const result = await claim.mutateAsync(inviteId);
      toast.success(result.alreadyMember ? "Already a member" : `Joined ${result.solutionName}`);
      navigate({
        to: "/s/$solutionId/intake",
        params: { solutionId: result.solutionId },
      });
    } catch {
      // toasted by the mutation
    }
  }

  return (
    <section className="rounded-xl border border-nymbl-blue/30 bg-nymbl-sky/60 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-nymbl-navy" />
        <h2 className="font-display text-sm font-semibold text-nymbl-navy">
          {invites.length === 1 ? "You have an invite waiting" : "You have invites waiting"}
        </h2>
      </div>
      <ul className="divide-y divide-[var(--color-border)] rounded-lg bg-card border border-[var(--color-border)]">
        {invites.map((inv) => (
          <li key={inv.id} className="px-4 py-3 flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">
                {inv.solutionName ?? "Solution"}
                {inv.clientName && (
                  <span className="text-muted-foreground"> · {inv.clientName}</span>
                )}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {inv.invitedByName
                  ? `Invited by ${inv.invitedByName}`
                  : inv.invitedByEmail
                    ? `Invited by ${inv.invitedByEmail}`
                    : "Invited"}{" "}
                · expires {new Date(inv.expiresAt).toLocaleDateString()}
              </div>
            </div>
            <Button size="sm" onClick={() => handleClaim(inv.id)} disabled={claim.isPending}>
              {claim.isPending ? "Joining…" : "Accept"}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RowView({ solutions }: { solutions: Solution[] }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-card overflow-hidden">
      <div className="grid grid-cols-[1fr_1fr_120px_120px_120px_36px] gap-3 items-center px-3 py-2 border-b border-[var(--color-border)] text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
        <span>Client</span>
        <span>Solution</span>
        <span>Phase</span>
        <span>Baseline</span>
        <span>Last activity</span>
        <span aria-hidden />
      </div>
      <ul>
        {solutions.map((s) => (
          <li key={s.id}>
            <Link
              to="/s/$solutionId/intake"
              params={{ solutionId: s.id }}
              className="grid grid-cols-[1fr_1fr_120px_120px_120px_36px] gap-3 items-center px-3 py-2.5 border-b border-[var(--color-border)] last:border-b-0 hover:bg-muted/40 transition-colors"
            >
              <span className="text-sm font-medium text-foreground truncate" title={s.clientName}>
                {s.clientName}
              </span>
              <span
                className="text-sm text-foreground/85 font-display font-semibold truncate"
                title={s.name}
              >
                {s.name}
              </span>
              <span>
                <span
                  className={cn(
                    "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold",
                    STAGE_TONE[s.stage],
                  )}
                >
                  {STAGE_LABEL[s.stage]}
                </span>
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {s.baselineVersion ?? "—"}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {relativeTime(s.lastActivity)}
              </span>
              <span className="text-muted-foreground inline-flex justify-end">
                <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
